import { memo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { CompleteBookingDialog } from './CompleteBookingDialog'
import type { CachedChatDisplayData } from '@/lib/chatListCache'
import { cn } from '@/lib/utils'

interface ChatListItemProps {
  displayData: CachedChatDisplayData
  isArtistView?: boolean
  onBookingComplete?: () => void
  /** Smaller row (e.g. profile) — matches legacy w-18 / h-18 list density */
  compact?: boolean
}

export const ChatListItem = memo(function ChatListItem({ 
  displayData,
  onBookingComplete,
  compact = false,
}: ChatListItemProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false)
  const bookingActive =
    displayData.hasBooking && displayData.bookingStatus !== 'completed'

  const receipt = displayData.messageReceipt ?? null
  const previewLine =
    receipt === 'sent'
      ? t('chats.receiptSent')
      : receipt === 'seen'
        ? t('chats.receiptSeen')
        : receipt === 'availability_sent'
          ? t('chats.receiptAvailabilitySent')
          : receipt === 'availability_seen'
            ? t('chats.receiptAvailabilitySeen')
            : displayData.lastMessagePreview || t('chats.listNoMessagesYet')

  const isPeerUnreadLine = displayData.hasUnread && receipt === null
  
  // Only fetch booking details if chat has a booking
  
  const handleChatClick = () => {
    navigate(`/chats/${displayData.chatId}`, {
      state: {
        cachedDisplayData: displayData
      }
    })
  }

  const handleCompleteBooking = (data: {
    status: 'completed' | 'no_show'
    finalPrice?: number
    actualDate?: Date
    notes?: string
  }) => {
    console.log('Booking completed:', data)
    // Trigger refresh of booking statuses
    onBookingComplete?.()
  }
  
  const renderAvatar = () => {
    return (
      <div
        className={cn(
          'overflow-hidden bg-border flex items-center justify-center',
          compact ? 'w-18 h-18' : 'w-20 h-20 md:w-18 md:h-18',
        )}
      >
        {displayData.avatarUrl ? (
          <img
            src={displayData.avatarUrl}
            alt={displayData.displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <span
            className={cn(
              'text-foreground font-semibold',
              compact ? 'text-sm' : 'text-lg',
            )}
          >
            {displayData.avatarInitial}
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      onClick={handleChatClick}
      className={cn(
        'grid grid-cols-[auto_1fr] items-center gap-x-4 hover:bg-muted/50 cursor-pointer transition-colors rounded-lg bg-card overflow-hidden',
        compact
          ? bookingActive
            ? 'min-h-18'
            : 'h-18'
          : 'h-20 md:h-18',
      )}
    >
      {/* Avatar - square, responsive size */}
      <div className="relative shrink-0">
        {renderAvatar()}
      </div>
      
      {/* Chat Content */}
      <div
        className={cn(
          'min-w-0 flex flex-col relative gap-0.5',
          compact ? 'py-1.5 pr-2' : 'py-2 pr-3',
          bookingActive ? 'justify-start' : 'justify-center',
        )}
      >
        {/* Row 1: Name */}
        <div className="flex items-center min-w-0">
          <h3 className="font-medium text-foreground text-sm truncate">
            {displayData.displayName}
          </h3>
        </div>
        
        {/* Row 2: Message preview / receipt */}
        <p
          className={cn(
            'text-sm line-clamp-1',
            isPeerUnreadLine ? 'font-medium text-foreground' : 'text-muted-foreground',
          )}
        >
          {previewLine}
        </p>

        {/* Row 3: Booked badge - only show for active bookings (not completed) */}
        {bookingActive && (
          <span
            className={cn(
              'font-medium text-green-500 pointer-events-none flex-shrink-0',
              compact ? 'text-xs' : 'text-sm',
            )}
          >
            Booked
          </span>
        )}

        {/* Check icon - positioned absolutely to mirror time position - only show for active bookings (not completed) */}
        {bookingActive && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsCompleteDialogOpen(true)
            }}
            className={cn(
              'absolute rounded-md bg-green-500 hover:bg-green-600 transition-colors flex-shrink-0',
              compact
                ? 'bottom-1 right-1 px-1.5 py-0.5'
                : 'bottom-3 right-3 px-2 py-1',
            )}
            title="Mark as complete"
          >
            <Check className={cn('text-white', compact ? 'w-3.5 h-3.5' : 'w-4 h-4')} />
          </button>
        )}
      </div>

      {/* Complete Booking Dialog */}
      <CompleteBookingDialog
        isOpen={isCompleteDialogOpen}
        onOpenChange={setIsCompleteDialogOpen}
        chatId={displayData.chatId}
        bookingId={displayData.bookingId || null}
        onSubmit={handleCompleteBooking}
      />
    </div>
  )
}) 